import { useCallback, useRef } from "react";
import jwt_decode from "jwt-decode";
import { useAuth } from "./AuthProvider.js";
import { getItem } from "./SessionStorage.js";
import { sharedRefresh } from "./refreshSession.js";
import { authDataFromToken, expectedAdminIdentity, identityMatches } from "./sessionIdentity.js";

const requestRefresh = async ({ api, setAuth, getRefreshHeaders, endSession }, opts = {}) => {
  const { savedAdminToken, impersonation } = opts;
  const authData = getItem("authToken");
  let response;
  try {
    // called when tour initial req failed due to expired access token, then we can retry the initial request
    response = await api.get("/refresh", {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${authData?.accessToken}`,
        ...getRefreshHeaders?.(opts),
      },
    });
  } catch (err) {
    // marker: gli interceptor non devono rielaborare questo errore
    err.isRefreshError = true;
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      // refresh rifiutato: il BE ha gia' cancellato il cookie
      endSession();
      err.sessionEnded = true;
    }
    throw err;
  }
  const decoded = response?.data?.accessToken ? jwt_decode(response.data.accessToken) : undefined;

  // Uscita da un'impersonazione: il token restituito deve essere dell'admin
  // (il cookie e' di chi ha fatto login, ma puo' essere cambiato da un altro
  // login nello stesso browser). Se non coincide NON si usa e si chiude la
  // sessione. Niente /auth/logout: il cookie potrebbe essere di un'altra utenza.
  const expected = expectedAdminIdentity({ sessionToken: authData?.accessToken, savedAdminToken, impersonation });
  if (expected && !identityMatches(response.data.accessToken, expected)) {
    endSession();
    const err = new Error("refresh identity mismatch");
    err.isRefreshError = true;
    err.sessionEnded = true;
    err.identityMismatch = true;
    throw err;
  }

  // stessa forma di auth dell'helper unico (ClientList, AuthProvider)
  // ✅ rbac_roles non si salvano dal JWT: li carica useRbacSync (evita
  // "400 Request Header Too Large" di nginx al refresh della pagina)
  const data = decoded?.UserInfo ? authDataFromToken(response.data.accessToken) : {};
  setAuth(data);
  return response.data.accessToken; // override the old access token
};

// `refresh` e' stabile (stessa identita' a ogni render) e condivide una sola
// promise in volo per tab: interceptor, PersistentLogin e socket la riusano.
// api: axios instance of the app API. getRefreshHeaders(opts): extra headers (e.g. the active winery).
// endSession: the app's useEndSession callback, called when the refresh is rejected.
const useRefreshToken = ({ api, getRefreshHeaders, endSession }) => {
  const { setAuth } = useAuth();
  const latest = useRef({});
  latest.current = { api, setAuth, getRefreshHeaders, endSession };

  // opts: { savedAdminToken, impersonation } solo per l'uscita da un'impersonazione
  // (controllo dell'identita' del token restituito)
  return useCallback((opts) => sharedRefresh(() => requestRefresh(latest.current, opts)), []);
};

export default useRefreshToken;
